import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { AnhangArten } from '@/base/dropzone/useFileUpload'

// Ein per Strg+V eingefügter, noch nicht hochgeladener Anhang auf einem neuen
// Objekt. Wird in der Navigation unter der DropZone im gleichen Stil wie die
// hochgeladenen Dateien angezeigt (siehe BaseLayoutForm / BaseAppLayout).
export interface DropZonePendingFile {
  // eindeutiger Schlüssel (Dateiname + Größe) – auch für das Entfernen genutzt
  key: string
  name: string
  groesse: string
  icon: string
}

/**
 * Im AppView-Store finden sich alle Einstellungen bezüglich globalem Layout und Ansicht wieder,
 * die zu keinen der anderen Stores passen
 */
export const useAppViewStore = defineStore('appView', () => {
  // States
  // ViewName & Title
  const uniqueViewName = ref('')
  const appBarEnabled = ref(true)
  const title = ref('')
  const subtitle = ref('')
  // Suchleisten
  const searchQuery = ref('')
  const searchCardQuery = ref<Record<string, string>>({})
  const showSearchBar = ref(false)
  // DropZone
  const dropZoneEnabled = ref(false)
  const dropZoneUrl = ref<string | null>(null)
  // Welche Anhänge das Objekt der DropZone annimmt (siehe BaseLayoutForm)
  const dropZoneAnhangArten = ref<AnhangArten>({ bilder: true, dokumente: true })
  // Per Strg+V eingefügte, noch nicht hochgeladene Anhänge auf einem neuen
  // Objekt (siehe BaseLayoutForm). Werden unter der DropZone in der Navigation
  // im gleichen Stil wie die hochgeladenen Dateien angezeigt.
  const dropZonePendingFiles = ref<DropZonePendingFile[]>([])
  // FloatButton
  const showFloatButton = ref(false)

  // Actions
  // ViewName & Title
  function setUniqueViewName(payload: string) {
    uniqueViewName.value = payload
  }
  function setAppBarEnabled(payload: boolean) {
    appBarEnabled.value = payload
  }
  function setTitle(payload: string) {
    title.value = payload
  }
  function setSubTitle(payload: string) {
    subtitle.value = payload
  }
  // Suchleisten
  function setSearchQuery(payload: string) {
    searchQuery.value = payload
  }
  function setSearchCardQuery(card: string, payload: string) {
    searchCardQuery.value[card] = payload
  }
  function resetSearchCardQueries() {
    Object.keys(searchCardQuery.value).forEach(key => { delete searchCardQuery.value[key] })
  }
  function setShowSearchBar(payload: boolean) {
    showSearchBar.value = payload
  }
  // DropZone
  function setDropZone(payload: boolean) {
    dropZoneEnabled.value = payload
  }
  function setDropZoneUrl(payload: string) {
    dropZoneUrl.value = payload
  }
  function setDropZoneAnhangArten(payload: AnhangArten) {
    dropZoneAnhangArten.value = payload
  }
  function setDropZonePendingFiles(payload: DropZonePendingFile[]) {
    dropZonePendingFiles.value = payload
  }
  // FloatButton
  function setShowFloatButton(payload: boolean) {
    showFloatButton.value = payload
  }

  // Getter
  const getSearchCardQuery = computed(() => (card: string) => searchCardQuery.value[card])

  return {
    uniqueViewName,
    appBarEnabled,
    title,
    subtitle,
    searchQuery,
    searchCardQuery,
    showSearchBar,
    dropZoneEnabled,
    dropZoneUrl,
    dropZoneAnhangArten,
    dropZonePendingFiles,
    showFloatButton,
    setUniqueViewName,
    setAppBarEnabled,
    setTitle,
    setSubTitle,
    setSearchQuery,
    setSearchCardQuery,
    resetSearchCardQueries,
    setShowSearchBar,
    setDropZone,
    setDropZoneUrl,
    setDropZoneAnhangArten,
    setDropZonePendingFiles,
    setShowFloatButton,
    getSearchCardQuery
  }
})